import React, { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import COLORS from '../../../constants/colors';
import { SectionCard } from '../../../components/Section';
import { useFetchPlanRulesHistoryQuery } from '../../../redux/features/planRules/planRules.apiSlice';
import { PlanRulesView } from '../../../types/planRules';
import { HISTORY_PAGE_SIZE, HISTORY_TABLE_LABELS } from './premium.constants';
import { historyRowText } from './premium.helpers';

interface ChangeHistoryProps {
  view: PlanRulesView;
  productName: (id: number) => string;
}

/** Every published change: who, when, what, before → after. Read-only. */
export function ChangeHistory({ view, productName }: ChangeHistoryProps) {
  const [tableName, setTableName] = useState('');
  const [page, setPage] = useState(0);
  const { data, isFetching, error } = useFetchPlanRulesHistoryQuery({
    tableName: tableName || undefined,
    page: page + 1,
    limit: HISTORY_PAGE_SIZE,
  });

  return (
    <SectionCard
      title="Change history"
      description="Every published change: who, when, what, before → after. Read-only."
      action={
        <Select
          size="small"
          displayEmpty
          value={tableName}
          onChange={(event) => {
            setTableName(event.target.value);
            setPage(0);
          }}
          inputProps={{ 'aria-label': 'Filter history' }}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">Everything</MenuItem>
          {Object.entries(HISTORY_TABLE_LABELS).map(([value, label]) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </Select>
      }
      disablePadding
    >
      {isFetching && <LinearProgress />}
      {error && (
        <Alert severity="error" sx={{ m: '16px' }}>
          Could not load the history.
        </Alert>
      )}
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" aria-label="Change history">
          <TableHead>
            <TableRow>
              <TableCell>When</TableCell>
              <TableCell>Who</TableCell>
              <TableCell>What</TableCell>
              <TableCell>Change</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data?.items ?? []).map((item) => {
              const text = historyRowText(item, view, productName);

              return (
                <TableRow key={item.id}>
                  <TableCell sx={{ whiteSpace: 'nowrap', color: COLORS.LIGHT_GRAY, verticalAlign: 'top' }}>
                    {new Date(item.changedAt).toLocaleString('en-GB', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top' }}>Employee #{item.changedBy}</TableCell>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    {text.where}
                    {item.note && (
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', color: COLORS.LIGHT_GRAY, fontStyle: 'italic' }}
                      >
                        “{item.note}”
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <Box component="span" sx={{ color: COLORS.LIGHT_GRAY, textDecoration: 'line-through' }}>
                      {text.from}
                    </Box>
                    {' → '}
                    <Box component="span" sx={{ fontWeight: 700 }}>
                      {text.to}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
            {!isFetching && !data?.items?.length && (
              <TableRow>
                <TableCell colSpan={4} sx={{ color: COLORS.LIGHT_GRAY }}>
                  No changes yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
      <TablePagination
        component="div"
        count={data?.totalItems ?? 0}
        page={page}
        rowsPerPage={HISTORY_PAGE_SIZE}
        rowsPerPageOptions={[HISTORY_PAGE_SIZE]}
        onPageChange={(event, next) => setPage(next)}
      />
    </SectionCard>
  );
}
